// src/components/dashboards/versions/VersionHistoryDrawer.tsx
//
// A dashboard's version history (plan 2026-10-05-dashboard-version-history,
// §A6; Looker Studio's history panel, not copied literally). A right-hand
// drawer: **Current** at the top, then every version grouped by day, newest
// first, with **Load more** at the bottom. Selecting a version loads what
// restoring it would change (the server's structural summary; the raw
// snapshot never reaches the client) and offers the actions the caller's
// `permissions` allow:
//
//   * **Restore this version** (`restore_version`): the server keeps the
//     current state as a restore point first, so the restore can be undone.
//   * **Open as copy** (everyone who can see the version): a new personal
//     dashboard made from it, which is how a version is LOOKED AT with live
//     data without new rendering code.
//   * **Name this version** (`save_version`): rename or clear.
//
// PER EDITING MODE (B-S4). In publishing mode the restore is labelled
// **Restore as draft** (it always went into the draft; the published version
// does not change), and **Publish this version** puts the version live on the
// published row, leaving the draft alone. Only on the draft (the row whose
// revision the server checks), only without review and only with
// `permissions.publish`; with review required the path is restore, then
// submit. In live mode the restore is as before.
//
// WHO SEES WHAT. `view_history` asks for every kind (`scope=all`); without
// it the request is `scope=published`, the only scope a reader is allowed
// (`scope=all` is 403 for them). The drawer guesses nothing beyond that: a
// button the server would refuse is not shown.
import { kindCopy } from "@/utils/kindCopy";
import { useKindCopy } from "@/context/DashboardKindContext";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconHistory, IconPencil, IconUpload } from "@tabler/icons-react";

import {
  copyVersion,
  getVersion,
  listVersions,
  renameVersion,
  restoreVersion,
  type DashboardVersion,
  type DashboardVersionChanges,
  type DashboardVersionDetail,
  type DashboardVersionKind,
} from "@/api/dashboardVersions";
import { AppDrawer } from "@/components/drawers/AppDrawer";
import { publishDashboardVersion } from "@/api/dashboardEditingModes";
import { useConfirmDialog } from "@/components/modals/confirmDialog";
import type { DashboardCopyResponse, DashboardDTO, DashboardPermissions } from "@/api/dashboards";
import { ApiError } from "@/api/client";

/** One page of the list. */
export const VERSION_PAGE_SIZE = 30;

/** The permissions block as the backend sends it since the version history
 *  landed. Optional here because `DashboardPermissions` (api/dashboards.ts)
 *  predates the two fields; absent reads as "not allowed". */
type VersionPermissions = DashboardPermissions & {
  view_history?: boolean;
  save_version?: boolean;
};

export type VersionHistoryDrawerProps = {
  opened: boolean;
  onClose: () => void;
  dashboard: DashboardDTO;
  /** After a successful restore: reload the dashboard (its tiles changed). */
  onRestored: () => Promise<void> | void;
  /** A failed restore. The caller's transition-error handler: a 409 means the
   *  dashboard changed meanwhile, and it shows the notice and reloads. */
  onRestoreError: (err: unknown) => void;
  /** Open as copy made a new dashboard: go there. */
  onOpenDashboard: (dashboardId: string) => void;
};

const KIND_LABEL: Record<Exclude<DashboardVersionKind, "published">, string> = {
  named: "Named",
  auto: "Auto-saved",
  restore_point: "Before restore",
  edition: "Edition",
};

const KIND_COLOR: Record<DashboardVersionKind, string> = {
  published: "teal",
  named: "blue",
  auto: "gray",
  restore_point: "orange",
  edition: "grape",
};

export function versionBadgeLabel(version: Pick<DashboardVersion, "kind" | "version_number">): string {
  return version.kind === "published" ? `Published v${version.version_number}` : KIND_LABEL[version.kind];
}

function versionTitle(version: DashboardVersion): string {
  return version.name || `Version ${version.version_number}`;
}

function versionAt(version: DashboardVersion): string | null {
  return version.created_at ?? version.approved_at ?? null;
}

function versionAuthor(version: DashboardVersion): string | null {
  return version.created_by_name ?? version.approved_by_name ?? null;
}

function dayKey(iso: string | null): string {
  if (!iso) return "unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Today", "Yesterday", else the local date. */
export function dayLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso || dayKey(iso) === "unknown") return "Earlier";
  const key = dayKey(iso);
  if (key === dayKey(now.toISOString())) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === dayKey(yesterday.toISOString())) return "Yesterday";
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function timeLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Consecutive versions of the same local day, in list order. */
function groupByDay(versions: DashboardVersion[]): { key: string; label: string; versions: DashboardVersion[] }[] {
  const groups: { key: string; label: string; versions: DashboardVersion[] }[] = [];
  for (const version of versions) {
    const at = versionAt(version);
    const key = dayKey(at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.versions.push(version);
    else groups.push({ key, label: dayLabel(at), versions: [version] });
  }
  return groups;
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && typeof err.detail === "string" && err.detail) return err.detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

export default function VersionHistoryDrawer(props: VersionHistoryDrawerProps) {
  return (
    <AppDrawer
      opened={props.opened}
      onClose={props.onClose}
      title="Version history"
      leading={<IconHistory size={22} />}
      size="narrow"
    >
      {/* Mounted only while open: every opening starts from a fresh first
          page, and a closed drawer holds no stale list. */}
      {props.opened && <VersionHistoryBody {...props} />}
    </AppDrawer>
  );
}

function VersionHistoryBody({
  onClose,
  dashboard,
  onRestored,
  onRestoreError,
  onOpenDashboard,
}: VersionHistoryDrawerProps) {
  const words = kindCopy(dashboard.kind);
  const p = (dashboard.permissions ?? {}) as Partial<VersionPermissions>;
  const fullHistory = !!p.view_history;
  const canRestore = !!p.restore_version;
  const canRename = !!p.save_version;
  const publishingMode = !!dashboard.publishing_enabled;
  // The draft is the personal row; the server checks ITS revision.
  const canPublishVersion =
    publishingMode && dashboard.scope_type === "user" && !dashboard.require_review && !!p.publish;

  const confirm = useConfirmDialog();
  const [versions, setVersions] = useState<DashboardVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  // Every list request carries a generation, so a slow first page cannot land
  // on top of a retry's newer one, nor a Load more on top of either.
  const generation = useRef(0);

  // Retry bumps this; the effect below is the one place a first page is
  // requested. State is set only when the request answers: the list is
  // already in its loading state (the initial state, or `retry`).
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const gen = ++generation.current;
    listVersions(dashboard.id, { scope: fullHistory ? "all" : "published", limit: VERSION_PAGE_SIZE })
      .then((page) => {
        if (gen !== generation.current) return;
        setVersions(page);
        setHasMore(page.length === VERSION_PAGE_SIZE);
      })
      .catch((err) => {
        if (gen !== generation.current) return;
        setLoadError(errorText(err, "Could not load the version history."));
      })
      .finally(() => {
        if (gen === generation.current) setLoading(false);
      });
  }, [dashboard.id, fullHistory, attempt]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    setVersions([]);
    setSelected(null);
    setAttempt((n) => n + 1);
  };

  const loadMore = async () => {
    if (versions.length === 0) return;
    const gen = generation.current;
    const before = Math.min(...versions.map((v) => v.version_number));
    setLoadingMore(true);
    try {
      const page = await listVersions(dashboard.id, {
        scope: fullHistory ? "all" : "published",
        before,
        limit: VERSION_PAGE_SIZE,
      });
      if (gen !== generation.current) return;
      setVersions((prev) => [...prev, ...page]);
      setHasMore(page.length === VERSION_PAGE_SIZE);
    } catch (err) {
      notifications.show({ color: "red", title: "Could not load more versions", message: errorText(err, "Please try again.") });
    } finally {
      setLoadingMore(false);
    }
  };

  const replaceVersion = (next: DashboardVersion) =>
    setVersions((prev) => prev.map((v) => (v.version_number === next.version_number ? next : v)));

  const groups = useMemo(() => groupByDay(versions), [versions]);

  const restore = (version: DashboardVersion) =>
    confirm({
      title: publishingMode ? `Restore v${version.version_number} as draft?` : `Restore v${version.version_number}?`,
      children: publishingMode ? (
        <Text size="sm">
          The draft is saved as a version first, then returns to {versionTitle(version)}. The published version
          does not change until you publish the draft.
        </Text>
      ) : (
        <Text size="sm">
          Your current state is saved as a version first. {words.say("The dashboard")} then returns to{" "}
          {versionTitle(version)}.
        </Text>
      ),
      labels: { confirm: "Restore", cancel: "Cancel" },
      confirmColor: "blue",
      onConfirm: async () => {
        try {
          const result = await restoreVersion(dashboard.id, version.version_number, dashboard.revision);
          notifications.show({
            color: "green",
            message: `Restored v${version.version_number}. Your previous state was saved as v${result.restore_point_version_number}.`,
          });
          const lost = result.tiles_lost_reference ?? [];
          if (lost.length > 0) {
            notifications.show({
              color: "yellow",
              title: `${plural(lost.length, "tile")} lost their data`,
              message: `Their data source or source chart no longer exists: ${lost.join(", ")}.`,
              autoClose: false,
            });
          }
          if (result.lens_unavailable) {
            notifications.show({
              color: "yellow",
              title: "Lens no longer available",
              message: "The lens this version used was removed or can no longer be used here. The report goes back to its platform lens.",
              autoClose: false,
            });
          }
          onClose();
          await onRestored();
        } catch (err) {
          onClose();
          onRestoreError(err);
        }
      },
    });

  const publishVersion = (version: DashboardVersion) =>
    confirm({
      title: `Publish v${version.version_number}?`,
      children: (
        <Text size="sm">
          Viewers see {versionTitle(version)} from now on. Your draft does not change.
        </Text>
      ),
      labels: { confirm: "Publish", cancel: "Cancel" },
      confirmColor: "blue",
      onConfirm: async () => {
        try {
          await publishDashboardVersion(dashboard.id, version.version_number, dashboard.revision);
          notifications.show({ color: "green", message: `Published v${version.version_number}.` });
          onClose();
          await onRestored();
        } catch (err) {
          onClose();
          onRestoreError(err);
        }
      },
    });

  const openAsCopy = async (version: DashboardVersion) => {
    let result: DashboardCopyResponse;
    try {
      result = await copyVersion(dashboard.id, version.version_number);
    } catch (err) {
      notifications.show({ color: "red", title: "Could not open a copy", message: errorText(err, "Please try again.") });
      return;
    }
    notifications.show(
      result.skipped_items > 0
        ? {
            color: "yellow",
            title: "Copy opened",
            message: `${plural(result.skipped_items, "tile")} left out: you do not have access to their data source.`,
          }
        : { color: "green", title: "Copy opened", message: kindCopy(result.dashboard.kind ?? dashboard.kind).copyListMessage(result.dashboard.name) }
    );
    onClose();
    onOpenDashboard(result.dashboard.id);
  };

  let body: ReactElement;
  if (loading) {
    body = (
      <Stack gap="sm" data-testid="version-history-loading">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={48} radius="sm" />
        ))}
      </Stack>
    );
  } else if (loadError) {
    body = (
      <Alert color="red" variant="light" title="Could not load the version history">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{loadError}</Text>
          <Button size="xs" variant="light" color="red" onClick={retry}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  } else {
    body = (
      <Stack gap="md">
        <Paper
          withBorder
          p="sm"
          radius="sm"
          data-testid="version-current"
          style={selected === null ? { borderColor: "var(--mantine-color-blue-5)" } : undefined}
        >
          <UnstyledButton onClick={() => setSelected(null)} w="100%">
            <Group gap="xs" wrap="nowrap">
              <Badge size="sm" variant="filled" color="blue">
                Current
              </Badge>
              <Text size="sm" fw={500}>
                {words.say("The dashboard as it is now")}
              </Text>
            </Group>
          </UnstyledButton>
        </Paper>
        {versions.length === 0 ? (
          <Text c="dimmed" size="sm" data-testid="version-history-empty">
            {fullHistory
              ? "No earlier versions yet. Verity saves a version after each editing session."
              : "No published versions yet."}
          </Text>
        ) : (
          groups.map((group) => (
            <Stack key={group.key + group.versions[0].version_number} gap={6} data-testid="version-day">
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                {group.label}
              </Text>
              {group.versions.map((version) => (
                <VersionEntry
                  key={version.version_number}
                  dashboardId={dashboard.id}
                  version={version}
                  selected={selected === version.version_number}
                  onSelect={() =>
                    setSelected((cur) => (cur === version.version_number ? null : version.version_number))
                  }
                  canRestore={canRestore}
                  canRename={canRename}
                  publishingMode={publishingMode}
                  canPublish={canPublishVersion}
                  onRestore={() => restore(version)}
                  onPublish={() => publishVersion(version)}
                  onOpenAsCopy={() => openAsCopy(version)}
                  onRenamed={replaceVersion}
                />
              ))}
            </Stack>
          ))
        )}
        {hasMore && (
          <Button variant="default" size="xs" onClick={() => void loadMore()} loading={loadingMore}>
            Load more
          </Button>
        )}
      </Stack>
    );
  }

  return body;
}

type VersionEntryProps = {
  dashboardId: string;
  version: DashboardVersion;
  selected: boolean;
  onSelect: () => void;
  canRestore: boolean;
  canRename: boolean;
  /** Publishing mode: the restore goes into the draft and says so. */
  publishingMode: boolean;
  /** "Publish this version" is offered. */
  canPublish: boolean;
  onRestore: () => void;
  onPublish: () => void;
  onOpenAsCopy: () => Promise<void>;
  onRenamed: (version: DashboardVersion) => void;
};

function VersionEntry({
  dashboardId,
  version,
  selected,
  onSelect,
  canRestore,
  canRename,
  publishingMode,
  canPublish,
  onRestore,
  onPublish,
  onOpenAsCopy,
  onRenamed,
}: VersionEntryProps) {
  const at = versionAt(version);
  const author = versionAuthor(version);
  const meta = [`v${version.version_number}`, author, timeLabel(at)].filter(Boolean).join(" · ");

  return (
    <Paper
      withBorder
      radius="sm"
      data-testid={`version-entry-${version.version_number}`}
      style={selected ? { borderColor: "var(--mantine-color-blue-5)" } : undefined}
    >
      <UnstyledButton onClick={onSelect} w="100%" p="sm" aria-expanded={selected}>
        <Stack gap={2}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Text size="sm" fw={500} truncate>
              {versionTitle(version)}
            </Text>
            <Badge size="sm" variant="light" color={KIND_COLOR[version.kind]} style={{ flexShrink: 0 }}>
              {versionBadgeLabel(version)}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {meta}
          </Text>
          {version.note && (
            <Text size="xs" c="dimmed" fs="italic">
              {version.note}
            </Text>
          )}
        </Stack>
      </UnstyledButton>
      {selected && (
        <VersionDetail
          dashboardId={dashboardId}
          version={version}
          canRestore={canRestore}
          canRename={canRename}
          publishingMode={publishingMode}
          canPublish={canPublish}
          onRestore={onRestore}
          onPublish={onPublish}
          onOpenAsCopy={onOpenAsCopy}
          onRenamed={onRenamed}
        />
      )}
    </Paper>
  );
}

function VersionDetail({
  dashboardId,
  version,
  canRestore,
  canRename,
  publishingMode,
  canPublish,
  onRestore,
  onPublish,
  onOpenAsCopy,
  onRenamed,
}: Omit<VersionEntryProps, "selected" | "onSelect">) {
  const [detail, setDetail] = useState<DashboardVersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [naming, setNaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    getVersion(dashboardId, version.version_number)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch((err) => {
        if (alive) setError(errorText(err, "Could not load this version."));
      });
    return () => {
      alive = false;
    };
  }, [dashboardId, version.version_number, attempt]);

  const saveName = async () => {
    setSavingName(true);
    try {
      const trimmed = nameDraft.trim();
      const next = await renameVersion(dashboardId, version.version_number, trimmed ? trimmed : null);
      onRenamed(next);
      setNaming(false);
    } catch (err) {
      notifications.show({ color: "red", title: "Could not name this version", message: errorText(err, "Please try again.") });
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Box px="sm" pb="sm" data-testid="version-detail">
      <Stack gap="sm">
        {error ? (
          <Alert color="red" variant="light">
            <Group gap="xs" justify="space-between">
              <Text size="sm">{error}</Text>
              <Button size="xs" variant="light" color="red" onClick={() => {
                  setError(null);
                  setDetail(null);
                  setAttempt((n) => n + 1);
                }}>
                Retry
              </Button>
            </Group>
          </Alert>
        ) : detail === null ? (
          <Stack gap={6} data-testid="version-detail-loading">
            <Skeleton height={12} width="70%" />
            <Skeleton height={12} width="50%" />
          </Stack>
        ) : (
          <ChangeSummary changes={detail.changes_from_current} />
        )}

        {naming && (
          <Stack gap={6}>
            <TextInput
              size="xs"
              label="Version name"
              placeholder="Leave empty to remove the name"
              value={nameDraft}
              maxLength={120}
              onChange={(e) => setNameDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveName();
                if (e.key === "Escape") setNaming(false);
              }}
              autoFocus
            />
            <Group gap="xs" justify="flex-end">
              <Button size="xs" variant="default" onClick={() => setNaming(false)} disabled={savingName}>
                Cancel
              </Button>
              <Button size="xs" onClick={() => void saveName()} loading={savingName}>
                Save name
              </Button>
            </Group>
          </Stack>
        )}

        <Group gap="xs">
          {canRestore && (
            <Button size="xs" leftSection={<IconHistory size={14} />} onClick={onRestore}>
              {publishingMode ? "Restore as draft" : "Restore this version"}
            </Button>
          )}
          {canPublish && (
            <Button size="xs" variant="light" leftSection={<IconUpload size={14} />} onClick={onPublish}>
              Publish this version
            </Button>
          )}
          <Button
            size="xs"
            variant="light"
            leftSection={<IconCopy size={14} />}
            loading={copying}
            onClick={async () => {
              setCopying(true);
              try {
                await onOpenAsCopy();
              } finally {
                setCopying(false);
              }
            }}
          >
            Open as copy
          </Button>
          {canRename && !naming && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconPencil size={14} />}
              onClick={() => {
                setNameDraft(version.name ?? "");
                setNaming(true);
              }}
            >
              Name this version
            </Button>
          )}
        </Group>
      </Stack>
    </Box>
  );
}

/** What restoring this version would change, by tab and tile name. */
export function ChangeSummary({ changes }: { changes: DashboardVersionChanges }) {
  const { say } = useKindCopy();
  if (!changes.count) {
    return (
      <Text size="sm" c="dimmed" data-testid="version-changes">
        {say("No differences from the current dashboard")}
      </Text>
    );
  }
  const rows: [string, string][] = [];
  const list = (label: string, names: string[] | undefined) => {
    if (names && names.length > 0) rows.push([label, names.join(", ")]);
  };
  list("Pages added", changes.tabs_added);
  list("Pages removed", changes.tabs_removed);
  if (changes.tabs_renamed && changes.tabs_renamed.length > 0) {
    rows.push(["Pages renamed", changes.tabs_renamed.map((r) => `${r.from} → ${r.to}`).join(", ")]);
  }
  list("Tiles added", changes.tiles_added);
  list("Tiles removed", changes.tiles_removed);
  list("Tiles changed", changes.tiles_changed);
  if (changes.filters_changed) rows.push(["Filters", "changed"]);
  list("Details changed", changes.details_changed);
  return (
    <Stack gap={4} data-testid="version-changes">
      <Text size="xs" c="dimmed">
        Restoring this version would change:
      </Text>
      {rows.map(([label, value]) => (
        <Text key={label} size="sm">
          <Text span fw={500}>
            {label}:
          </Text>{" "}
          {value}
        </Text>
      ))}
    </Stack>
  );
}
